import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CircleUserRound,
  GitMerge,
  ListTodo,
  Mail,
  Phone,
  Plus,
  Search,
  ShieldAlert,
  Star,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useContactList } from "@/hooks/useContacts";
import { useAuthStore } from "@/store/useAuthStore";
import { ContactCreateFlow } from "@/components/clients/ContactCreateFlow";
import type { ContactListRow, ContactScope } from "@/api/contacts";

const SCOPE_LABEL: Record<ContactScope, string> = {
  my: "Мої",
  team: "Команда",
  all: "Усі",
};

type ChipKey = "hasMethods" | "hasOpenTasks" | "needsReview" | "duplicateStatus";

const CHIP_DEFS: Array<{ k: ChipKey; label: string; facet: keyof FacetCounts }> = [
  { k: "needsReview", label: "Потребують перевірки", facet: "needsReview" },
  { k: "hasOpenTasks", label: "Відкриті задачі", facet: "hasOpenTasks" },
  { k: "duplicateStatus", label: "Дублікати", facet: "duplicates" },
  { k: "hasMethods", label: "З контактами", facet: "hasMethods" },
];

interface FacetCounts {
  needsReview: number;
  duplicates: number;
  hasOpenTasks: number;
  hasMethods: number;
}

/**
 * Phase 3.X-1B sub_1 — Contact LIST ("Контакти").
 *
 * One flat, ACL-scoped contact list (TZ §8). Mirrors the A1 ClientsList read-model:
 * backend owns scope, cursor pagination, facets, search, ordering. Default scope
 * comes from the backend per role (Manager→my, Supervisor→team, Director→team).
 *
 * §E2: phone/email are visible-only indicators — values live in the detail
 * (sub_2). The context chip is the A-block client (holding) name, distinct from
 * the contact's own name, and links into Client360.
 */
export function ContactsListPage() {
  const navigate = useNavigate();

  // "" = let the backend apply the per-role default scope on first load.
  const [scope, setScope] = useState<ContactScope | "">("");
  const [chips, setChips] = useState<Record<ChipKey, boolean>>({
    hasMethods: false,
    hasOpenTasks: false,
    needsReview: false,
    duplicateStatus: false,
  });
  const [manager, setManager] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState("");
  const [accumulated, setAccumulated] = useState<ContactListRow[]>([]);
  // FS-UX §0 empty-CTA: contacts are CRM-native (0 day-1) → live create affordance.
  const [createOpen, setCreateOpen] = useState(false);

  const query = useContactList({
    scope,
    manager,
    q,
    cursor,
    limit: 25,
    hasMethods: chips.hasMethods,
    hasOpenTasks: chips.hasOpenTasks,
    needsReview: chips.needsReview,
    duplicateStatus: chips.duplicateStatus,
  });
  const data = query.data;

  const allowedScopes = data?.allowedScopes ?? ["my"];
  const effectiveScope: ContactScope = (scope || data?.appliedScope || "my") as ContactScope;
  const canFilterManager = allowedScopes.includes("all");
  const managers = data?.managers ?? [];
  const facets: FacetCounts = data?.facets ?? {
    needsReview: 0,
    duplicates: 0,
    hasOpenTasks: 0,
    hasMethods: 0,
  };

  // Reset accumulation whenever a filter (not cursor) changes.
  const filterKey = `${scope}|${manager}|${q}|${chips.hasMethods}|${chips.hasOpenTasks}|${chips.needsReview}|${chips.duplicateStatus}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setCursor("");
    setAccumulated([]);
  }

  const rows = useMemo(() => {
    if (!data) return accumulated;
    if (cursor === "") return data.items;
    const seen = new Set(accumulated.map((r) => r.id));
    return [...accumulated, ...data.items.filter((r) => !seen.has(r.id))];
  }, [data, cursor, accumulated]);

  function loadMore() {
    if (data?.nextCursor) {
      setAccumulated(rows);
      setCursor(data.nextCursor);
    }
  }

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(qInput.trim());
  }

  function toggleChip(k: ChipKey) {
    setChips((c) => ({ ...c, [k]: !c[k] }));
  }

  function resetAll() {
    setChips({ hasMethods: false, hasOpenTasks: false, needsReview: false, duplicateStatus: false });
    setManager("");
    setQInput("");
    setQ("");
  }

  const activeChipCount = Object.values(chips).filter(Boolean).length;
  const activeFilterCount = activeChipCount + (manager ? 1 : 0) + (q ? 1 : 0);

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1080px]">
        <div className="mb-4">
          <h1 className="text-xl font-semibold tracking-tight">Контакти</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Контактні особи клієнтів. Пошук за іменем, телефоном, поштою або клієнтом.
          </p>
        </div>

        <div className="mb-3 rounded-2xl border bg-card p-3">
          <form onSubmit={submitSearch} className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Пошук за іменем, телефоном, поштою, клієнтом…"
              className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-10 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            {qInput && (
              <button
                type="button"
                onClick={() => { setQInput(""); setQ(""); }}
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
                title="Очистити"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border bg-background p-0.5">
              {allowedScopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={cn(
                    "rounded px-3 py-1 text-xs font-medium transition-colors",
                    effectiveScope === s
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {SCOPE_LABEL[s]}
                </button>
              ))}
            </div>

            {canFilterManager && (
              <select
                value={manager}
                onChange={(e) => { setManager(e.target.value); if (e.target.value) setScope("all"); }}
                className="h-9 rounded-md border border-input bg-background px-3 text-xs"
              >
                <option value="">Усі менеджери</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            )}

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetAll}
                className="ml-auto text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Скинути ({activeFilterCount})
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
            {CHIP_DEFS.map((c) => {
              const count = facets[c.facet];
              const on = chips[c.k];
              return (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => toggleChip(c.k)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                    on
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {c.label}{count > 0 ? ` (${count})` : ""}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-1.5 flex items-center justify-between px-1">
          <span className="text-sm text-muted-foreground">
            <b className="text-foreground">{data?.totalVisibleCount ?? 0}</b> знайдено
          </span>
        </div>

        {query.isPending ? (
          <ListSkeleton />
        ) : query.isError ? (
          <div className="rounded-2xl border bg-card px-4 py-12 text-center">
            <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
            <div className="text-sm font-medium">Не вдалося завантажити контакти</div>
            <button onClick={() => query.refetch()} className="mt-3 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">
              Повторити
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
            <Search className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <div className="text-sm font-medium">Контактів не знайдено</div>
            <div className="mt-1 text-sm text-muted-foreground">Спробуйте змінити пошук, фільтри або область — або додайте перший контакт.</div>
            <div className="mt-3 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />Створити контакт
              </button>
              {activeFilterCount > 0 && (
                <button onClick={resetAll} className="text-sm font-medium text-primary">Скинути фільтри</button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((c) => (
              <ContactRowCard
                key={c.id}
                row={c}
                onOpen={() => navigate(`/contacts/${c.id}`)}
                onOpenClient={c.clientId ? () => navigate(`/clients/${c.clientId}`) : undefined}
              />
            ))}
          </div>
        )}

        {data && rows.length > 0 && (
          <div className="mt-3 flex items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground">
              Показано <b className="text-foreground">{rows.length}</b> із <b className="text-foreground">{data.totalVisibleCount}</b>
            </span>
            {data.nextCursor && (
              <button
                onClick={loadMore}
                disabled={query.isFetching}
                className="rounded-md border bg-background px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                {query.isFetching ? "Завантаження…" : "Показати ще"}
              </button>
            )}
          </div>
        )}
      </div>

      <ContactCreateFlow open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function ContactRowCard({
  row,
  onOpen,
  onOpenClient,
}: {
  row: ContactListRow;
  onOpen: () => void;
  onOpenClient?: () => void;
}) {
  const subtitle = row.roleLabel || row.position;
  // BUNDLE 1 dual-path: OFF (default) = current rendering (zero regression);
  // ON = upgraded (primary badge, merge-count, «без доступних методів»).
  const customView = useAuthStore((s) => s.featureFlags?.customContactView ?? false);
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3 hover:border-primary/40">
      <button onClick={onOpen} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground" title="Відкрити контакт">
        <CircleUserRound className="h-5 w-5" />
      </button>

      <div className="min-w-0 flex-1">
        <button onClick={onOpen} className="block w-full cursor-pointer text-left" title="Відкрити контакт">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[15px] font-semibold">{row.contactDisplayName}</span>
            {customView && row.isPrimary && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                <Star className="h-3 w-3" /> Основний
              </span>
            )}
            {row.needsReview && (
              <span className="shrink-0 rounded-full bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground">
                Перевірити
              </span>
            )}
            {customView ? (
              row.duplicateCount > 0 && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground" title="Кандидати-дублікати">
                  <GitMerge className="h-3 w-3" /> {row.duplicateCount}
                </span>
              )
            ) : (
              row.hasDuplicates && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground">
                  <ShieldAlert className="h-3 w-3" /> Дублікат
                </span>
              )
            )}
            {!row.isActive && (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                неактивний
              </span>
            )}
          </div>
        </button>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {subtitle && <span>{subtitle}</span>}
          {row.clientDisplayName && (
            <>
              {subtitle && <span>·</span>}
              {onOpenClient ? (
                <button
                  onClick={onOpenClient}
                  className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20"
                  title="Відкрити картку клієнта"
                >
                  {row.clientDisplayName}
                </button>
              ) : (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                  {row.clientDisplayName}
                </span>
              )}
            </>
          )}
          {row.manager && <><span>·</span><span>{row.manager}</span></>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 text-muted-foreground">
        {row.hasPhone && <Phone className="h-4 w-4" aria-label="Має телефон" />}
        {row.hasEmail && <Mail className="h-4 w-4" aria-label="Має пошту" />}
        {customView && !row.hasPhone && !row.hasEmail && (
          <span className="hidden text-xs italic text-muted-foreground/60 sm:inline">без доступних методів</span>
        )}
        {row.hasOpenTasks && (
          <span className="inline-flex items-center gap-1 text-xs text-primary" title="Відкриті задачі">
            <ListTodo className="h-4 w-4" />
          </span>
        )}
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
          <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
          </div>
          <div className="hidden h-3.5 w-16 animate-pulse rounded bg-muted md:block" />
        </div>
      ))}
    </div>
  );
}
